import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthSessionStore } from '../stores/auth-session-store';
import type { RouteGuardProps } from '../type';

/**
 * Rendered while the session is still `'unknown'`.
 *
 * Returning `null` here rather than redirecting is the whole point of the
 * three-state status: a guard that treated "not yet checked" as "not signed in"
 * would bounce every signed-in user to the sign-in page on a hard refresh, then
 * bounce them back once the session resolved.
 */
const Pending = (): ReactNode => null;

/** Requires a signed-in session. */
export const PrivateRoute = ({ children, redirectTo }: RouteGuardProps): ReactNode => {
  const status = useAuthSessionStore((state) => state.status);
  const location = useLocation();

  if (status === 'unknown') return <Pending />;
  if (status === 'unauthenticated') {
    // `state.from` lets the sign-in screen send the user back where they were
    // headed instead of dumping everyone on the dashboard.
    return <Navigate to={redirectTo} replace state={{ from: location }} />;
  }
  return children;
};

/** Requires a signed-OUT session. Wraps sign-in, sign-up and password recovery. */
export const PublicRoute = ({ children, redirectTo }: RouteGuardProps): ReactNode => {
  const status = useAuthSessionStore((state) => state.status);

  if (status === 'unknown') return <Pending />;
  if (status === 'authenticated') return <Navigate to={redirectTo} replace />;
  return children;
};
